// Seller, segurança (`spec/24-croqui-alinhamento.md` §5, WEB-65; Zenith WEB-114). Igual
// ao frame `screens/seller/seguranca.tsx` do canvas em 1440: duas colunas (`1fr 1.3fr`),
// 2FA e senha à esquerda, sessões e segredo de postback à direita.
//
// Foco da tela: o **estado da 2FA** (Badge + ação primária no cabeçalho). Quem tem
// `useQuery`/`useMutation` é esta rota; os componentes de `features/security` recebem
// dado por prop.
//
// Sem skeleton único de página: as quatro queries (sessão, sessões, prefs, postback) são
// independentes e cada painel mostra o próprio carregando/erro (a "camada de dados fica",
// brief §4). O frame "Estado: carregando" do canvas é uma simplificação de mockup.
//
// 2FA e senha não têm step-up: o Better Auth pede a senha atual no corpo. As exceções
// são o `PUT` dos toggles (piso incondicional, FIX-SEC-02/MÉDIO-1) e a rotação do
// segredo de postback (categoria `webhook`, `spec/03` §4.3), nos dois a API devolve
// `403 mfa_required` e o `StepUpDialog` de `api/client.ts` reexecuta a chamada.
//
// O estado da 2FA sai de `GET /v1/me/auth/get-session` (`api/session.ts`), não de
// `GET /v1/me.two_factor_enabled`, ver "Questões abertas" da task.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useState } from 'react';
import { changePassword, enableTwoFactor, verifyTotp } from '../api/auth.ts';
import { errorMessageOf } from '../api/errors.ts';
import {
  type ActiveSession,
  disableTwoFactor,
  generateBackupCodes,
  revokeSession,
  SESSIONS_QUERY_KEY,
  STEP_UP_PREFS_QUERY_KEY,
  type StepUpPrefKey,
  sessionsQueryOptions,
  stepUpPrefsQueryOptions,
  updateStepUpPrefs,
} from '../api/queries/security.ts';
import { SESSION_QUERY_KEY, sessionQueryOptions } from '../api/session.ts';
import { useMode } from '../app/mode.tsx';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { Button } from '../components/ui/button.tsx';
import { ChangePasswordForm } from '../features/security/change-password-form.tsx';
import { isStepUpCancelled, manualTotpCode } from '../features/security/model.ts';
import {
  POSTBACK_SECRET_QUERY_KEY,
  postbackSecretQueryOptions,
  rotatePostbackSecret,
} from '../features/security/postback.ts';
import { PostbackSecretPanel } from '../features/security/postback-secret-panel.tsx';
import { SessionsPanel } from '../features/security/sessions-table.tsx';
import { StepUpPrefsSection } from '../features/security/step-up-prefs-section.tsx';
import {
  BackupCodesDrawer,
  DisableTwoFactorDrawer,
  EnableTwoFactorDrawer,
  type TwoFactorEnrollment,
} from '../features/security/two-factor-drawers.tsx';
import { TwoFactorPanel } from '../features/security/two-factor-panel.tsx';

type OpenDrawer = 'enable' | 'disable' | 'backup-codes';

function Security() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const session = useQuery(sessionQueryOptions);
  const sessions = useQuery(sessionsQueryOptions(mode));
  const prefs = useQuery(stepUpPrefsQueryOptions(mode));
  const postback = useQuery(postbackSecretQueryOptions(mode));

  const [drawer, setDrawer] = useState<OpenDrawer | undefined>(undefined);
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | undefined>(undefined);
  const [enrolledCodes, setEnrolledCodes] = useState<string[] | undefined>(undefined);
  const [freshBackupCodes, setFreshBackupCodes] = useState<string[] | undefined>(undefined);
  const [twoFactorError, setTwoFactorError] = useState<string | undefined>(undefined);
  const [sessionError, setSessionError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [passwordDone, setPasswordDone] = useState(false);
  const [busySessionId, setBusySessionId] = useState<string | undefined>(undefined);
  const [prefsError, setPrefsError] = useState<string | undefined>(undefined);
  const [secret, setSecret] = useState<string | undefined>(undefined);
  const [postbackError, setPostbackError] = useState<string | undefined>(undefined);

  const refreshSessions = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [SESSIONS_QUERY_KEY] });
  };

  const enable = useMutation({
    // `gcTime: 0`: sem isto o `MutationCache` guardaria os backup codes em claro por mais
    // 5 min depois de a gaveta fechar. Mesma decisão de `features/api-keys`.
    gcTime: 0,
    mutationFn: async (password: string) => {
      setTwoFactorError(undefined);
      const result = await enableTwoFactor(password);
      // Mesma geração de `/admin/setup-2fa`: QR no cliente a partir do `totpURI`
      // (por isso `img-src data:` na CSP, `spec/17` §9.3).
      return {
        enrollment: {
          qr: await QRCode.toDataURL(result.totpURI, { margin: 1, width: 200 }),
          manualCode: manualTotpCode(result.totpURI),
        } satisfies TwoFactorEnrollment,
        backupCodes: result.backupCodes,
      };
    },
    onSuccess: (result) => {
      setEnrollment(result.enrollment);
      // Guardados para o passo 3: `enable` é a ÚNICA resposta que traz estes códigos.
      setEnrolledCodes(result.backupCodes);
    },
    onError: (error) => {
      setTwoFactorError(errorMessageOf(error));
    },
  });

  const verify = useMutation({
    mutationFn: async (code: string) => {
      setTwoFactorError(undefined);
      return verifyTotp(code);
    },
    onSuccess: async () => {
      // Passo 3 da gaveta: os códigos que vieram do `enable` aparecem agora.
      setEnrollment(undefined);
      setFreshBackupCodes(enrolledCodes);
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      await refreshSessions();
    },
    onError: (error) => {
      setTwoFactorError(errorMessageOf(error));
    },
  });

  const disable = useMutation({
    mutationFn: async (password: string) => {
      setTwoFactorError(undefined);
      return disableTwoFactor(password);
    },
    onSuccess: async () => {
      // Desativar derruba TODAS as sessões, inclusive esta (`spec/03` §4.7): manda para o
      // login em vez de esperar o 401 do próximo polling.
      queryClient.clear();
      await navigate({ to: '/login' });
    },
    onError: (error) => {
      setTwoFactorError(errorMessageOf(error));
    },
  });

  const regenerate = useMutation({
    gcTime: 0,
    mutationFn: async (password: string) => {
      setTwoFactorError(undefined);
      return generateBackupCodes(password);
    },
    onSuccess: setFreshBackupCodes,
    onError: (error) => {
      setTwoFactorError(errorMessageOf(error));
    },
  });

  const revoke = useMutation({
    mutationFn: async (target: ActiveSession) => {
      setSessionError(undefined);
      setBusySessionId(target.id);
      return revokeSession(mode, target.id);
    },
    onError: (error) => {
      setSessionError(errorMessageOf(error));
    },
    onSettled: async () => {
      setBusySessionId(undefined);
      await refreshSessions();
    },
  });

  const password = useMutation({
    mutationFn: async (values: { currentPassword: string; newPassword: string }) => {
      setPasswordError(undefined);
      setPasswordDone(false);
      return changePassword(values);
    },
    onSuccess: async () => {
      setPasswordDone(true);
      await refreshSessions();
    },
    onError: (error) => {
      setPasswordError(errorMessageOf(error));
    },
  });

  /**
   * Piso incondicional (FIX-SEC-02/MÉDIO-1): qualquer `PUT` sem step-up recente devolve
   * `403 mfa_required`, que abre o `StepUpDialog` em `api/client.ts` e reexecuta a
   * chamada original (`spec/17` §4.3).
   */
  const savePrefs = useMutation({
    mutationFn: async (patch: Partial<Record<StepUpPrefKey, boolean>>) => {
      setPrefsError(undefined);
      return updateStepUpPrefs(mode, patch);
    },
    onSuccess: (next) => {
      queryClient.setQueryData([STEP_UP_PREFS_QUERY_KEY, mode], next);
    },
    onError: (error) => {
      setPrefsError(errorMessageOf(error));
    },
  });

  const rotate = useMutation({
    gcTime: 0,
    mutationFn: async () => {
      setPostbackError(undefined);
      return rotatePostbackSecret(mode);
    },
    onSuccess: async (rotated) => {
      setSecret(rotated);
      await queryClient.invalidateQueries({ queryKey: [POSTBACK_SECRET_QUERY_KEY] });
    },
    onError: (error) => {
      // Step-up cancelado não é falha: o usuário só desistiu.
      if (!isStepUpCancelled(error)) {
        setPostbackError(errorMessageOf(error));
      }
    },
  });

  /** Fecha a gaveta e apaga tudo que ela carregava (QR, códigos, erro). */
  const closeTwoFactorDrawer = (): void => {
    setDrawer(undefined);
    setEnrollment(undefined);
    setEnrolledCodes(undefined);
    setFreshBackupCodes(undefined);
    setTwoFactorError(undefined);
    enable.reset();
    regenerate.reset();
  };

  const enabled = session.data?.twoFactorEnabled === true;
  const rows = sessions.data ?? [];

  return (
    <>
      <PageActions>
        {
          // `session.data === undefined`: sem isto o botão primário pisca para quem já
          // tem a 2FA ligada, enquanto o `get-session` carrega.
          session.data === undefined || enabled ? undefined : (
            <Button
              size="sm"
              type="button"
              onClick={() => {
                setDrawer('enable');
              }}
            >
              Ativar 2FA
            </Button>
          )
        }
      </PageActions>

      <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[1fr_1.3fr]">
        <div className="flex min-w-0 flex-col gap-6">
          <TwoFactorPanel
            enabled={enabled}
            isLoading={session.isPending}
            // Erro só quando não há estado: um flake no refetch de fundo não deve trocar o
            // painel carregado por uma tela de falha.
            error={session.data === undefined ? session.error : undefined}
            onRetry={() => {
              void session.refetch();
            }}
            onEnable={() => {
              setDrawer('enable');
            }}
            onDisable={() => {
              setDrawer('disable');
            }}
            onBackupCodes={() => {
              setDrawer('backup-codes');
            }}
          />

          <ChangePasswordForm
            isPending={password.isPending}
            done={passwordDone}
            {...(passwordError === undefined ? {} : { errorMessage: passwordError })}
            onSubmit={(values) => {
              password.mutate(values);
            }}
          />

          <StepUpPrefsSection
            {...(prefs.data === undefined ? {} : { prefs: prefs.data })}
            isLoading={prefs.isPending}
            error={prefs.data === undefined ? prefs.error : undefined}
            onRetry={() => {
              void prefs.refetch();
            }}
            busy={savePrefs.isPending}
            {...(prefsError === undefined ? {} : { actionError: prefsError })}
            onToggle={(key, value) => {
              savePrefs.mutate({ [key]: value });
            }}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <SessionsPanel
            sessions={rows}
            isLoading={sessions.isPending}
            error={sessions.data === undefined ? sessions.error : undefined}
            onRetry={() => {
              void sessions.refetch();
            }}
            onRevoke={(target) => {
              revoke.mutate(target);
            }}
            {...(busySessionId === undefined ? {} : { busySessionId })}
            {...(sessionError === undefined ? {} : { errorMessage: sessionError })}
          />

          <PostbackSecretPanel
            prefix={postback.data?.prefix ?? null}
            isLoading={postback.isPending}
            error={postback.data === undefined ? postback.error : undefined}
            onRetry={() => {
              void postback.refetch();
            }}
            {...(secret === undefined ? {} : { secret })}
            isRotating={rotate.isPending}
            {...(postbackError === undefined ? {} : { errorMessage: postbackError })}
            onRotate={() => {
              rotate.mutate();
            }}
            onDismissSecret={() => {
              setSecret(undefined);
              rotate.reset();
            }}
          />
        </div>
      </div>

      <EnableTwoFactorDrawer
        open={drawer === 'enable'}
        onOpenChange={(open) => {
          if (!open) {
            closeTwoFactorDrawer();
          }
        }}
        {...(enrollment === undefined ? {} : { enrollment })}
        {...(freshBackupCodes === undefined ? {} : { backupCodes: freshBackupCodes })}
        isPending={enable.isPending || verify.isPending}
        {...(twoFactorError === undefined ? {} : { errorMessage: twoFactorError })}
        onEnable={(value) => {
          enable.mutate(value);
        }}
        onVerify={(code) => {
          verify.mutate(code);
        }}
      />

      <BackupCodesDrawer
        open={drawer === 'backup-codes'}
        onOpenChange={(open) => {
          if (!open) {
            closeTwoFactorDrawer();
          }
        }}
        {...(freshBackupCodes === undefined ? {} : { codes: freshBackupCodes })}
        isPending={regenerate.isPending}
        {...(twoFactorError === undefined ? {} : { errorMessage: twoFactorError })}
        onGenerate={(value) => {
          regenerate.mutate(value);
        }}
      />

      <DisableTwoFactorDrawer
        open={drawer === 'disable'}
        onOpenChange={(open) => {
          if (!open) {
            closeTwoFactorDrawer();
          }
        }}
        isPending={disable.isPending}
        {...(twoFactorError === undefined ? {} : { errorMessage: twoFactorError })}
        onDisable={(value) => {
          disable.mutate(value);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/security/')({ component: Security });
